import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Tag, Loader2, Shield } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LangSwitcher from "@/components/LangSwitcher";
import BlogLeadForm from "@/components/BlogLeadForm";
import BlogShareBar from "@/components/BlogShareBar";
import StickyLeadDrawer from "@/components/StickyLeadDrawer";
import NewsletterSignup from "@/components/NewsletterSignup";
import { getBlog, getMeta, getStoredLang, setStoredLang } from "@/lib/api";

const CAT_ACCENT = {
    know_your_rights: { label: "KNOW YOUR RIGHTS", color: "text-white", hex: "#FFFFFF" },
    in_the_moment: { label: "IN THE MOMENT", color: "text-[#FF3B30]", hex: "#FF3B30" },
    fight_back: { label: "FIGHT BACK", color: "text-[#FFCC00]", hex: "#FFCC00" },
    diy_pro_se: { label: "DIY PRO SE", color: "text-white", hex: "#FFFFFF" },
};

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [lang, setLang] = useState(getStoredLang());

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        getBlog(slug, lang)
            .then(setPost)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [slug, lang]);

    const onLangChange = (l) => {
        setStoredLang(l);
        setLang(l);
    };

    const rtl = lang === "ar";

    // Dynamic page title + meta tags (helps SEO + iMessage/Slack/WhatsApp previews
    // even though FB/LinkedIn crawlers won't run JS — those still benefit from a
    // server-side OG route if added later).
    useEffect(() => {
        if (!post) return;
        const prevTitle = document.title;
        const title = `${post.title} · HPM`;
        document.title = title;
        const upsert = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
            } else {
                Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            }
            return el;
        };
        const desc = post.subtitle || post.excerpt || "Higher Power Movement — free legal AI.";
        const url =
            typeof window !== "undefined"
                ? `${window.location.origin}/hpm-blog/${post.slug}`
                : `/hpm-blog/${post.slug}`;
        upsert('meta[name="description"]', { name: "description", content: desc.slice(0, 200) });
        upsert('meta[property="og:title"]', { property: "og:title", content: post.title });
        upsert('meta[property="og:description"]', { property: "og:description", content: desc.slice(0, 200) });
        upsert('meta[property="og:type"]', { property: "og:type", content: "article" });
        upsert('meta[property="og:url"]', { property: "og:url", content: url });
        upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
        upsert('meta[name="twitter:title"]', { name: "twitter:title", content: post.title });
        upsert('meta[name="twitter:description"]', { name: "twitter:description", content: desc.slice(0, 200) });
        return () => {
            document.title = prevTitle;
        };
    }, [post]);

    const cat = useMemo(() => {
        return (post && CAT_ACCENT[post.category]) || CAT_ACCENT.know_your_rights;
    }, [post]);

    // Mid-point insertion index for the inline lead form
    const sectionCount = post?.sections?.length || 0;
    const midIdx = Math.min(2, Math.max(0, Math.floor(sectionCount / 2)));

    if (loading) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="text-center py-32">
                    <Loader2 size={28} className="animate-spin mx-auto text-[#FF3B30]" />
                </div>
            </div>
        );
    }
    if (error || !post) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="text-center py-32">
                    <div className="font-anton text-3xl uppercase">Post not found.</div>
                    <Link to="/hpm-blog" className="btn-brutal mt-6 inline-flex">
                        ← BACK TO BLOG
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <article
                className="max-w-[900px] mx-auto px-6 sm:px-10 py-10 pb-32"
                dir={rtl ? "rtl" : "ltr"}
            >
                <Link
                    to="/hpm-blog"
                    className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white flex items-center gap-2 mb-6"
                    data-testid="back-to-blog"
                >
                    <ArrowLeft size={14} /> ALL ARTICLES
                </Link>

                <div className="mb-6">
                    <LangSwitcher
                        value={lang}
                        onChange={onLangChange}
                        pending={post.translation_pending}
                    />
                </div>

                <div className={`font-mono text-[11px] uppercase tracking-[0.3em] mb-5 ${cat.color}`}>
                    {cat.label} · {post.reading_time_minutes} MIN READ
                </div>

                <h1
                    className="font-anton uppercase leading-[0.95] text-5xl sm:text-6xl md:text-7xl"
                    data-testid="post-title"
                >
                    {post.title}
                </h1>
                {post.subtitle && (
                    <p className="mt-6 font-sans text-lg text-white/75 leading-relaxed border-l-4 border-[#FF3B30] pl-4">
                        {post.subtitle}
                    </p>
                )}

                {post.audience && (
                    <div className="mt-6 border-2 border-white/20 p-4">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
                            WRITTEN FOR
                        </div>
                        <div className="font-sans text-sm text-white/85">{post.audience}</div>
                    </div>
                )}

                {post.tags && post.tags.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-2">
                        {post.tags.map((t, i) => (
                            <span
                                key={i}
                                className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-white/30 text-white/70"
                            >
                                <Tag size={9} className="inline mr-1" /> {t}
                            </span>
                        ))}
                    </div>
                )}

                {/* SHARE BAR (above the fold) */}
                <div className="mt-8">
                    <BlogShareBar slug={post.slug} title={post.title} accent={cat.hex === "#FFFFFF" ? "#FF3B30" : cat.hex} />
                </div>

                <div className="my-10 border-t-2 border-white/20" />

                {/* SECTIONS with mid-article lead form */}
                <div className="space-y-10" data-testid="post-sections">
                    {(post.sections || []).map((s, i) => (
                        <div key={i}>
                            <section data-testid={`section-${i}`}>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-2">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <h2 className="font-anton text-3xl sm:text-4xl uppercase leading-tight mb-4">
                                    {s.heading}
                                </h2>
                                <div className="font-sans text-base text-white/85 leading-relaxed whitespace-pre-wrap">
                                    {s.body}
                                </div>
                            </section>
                            {i === midIdx && (
                                <div className="mt-10" data-testid="mid-article-lead">
                                    <BlogLeadForm
                                        slug={post.slug}
                                        topicKey={post.topic_key}
                                        category={cat.label}
                                        variant="inline"
                                        slot="mid"
                                        accent={cat.hex === "#FFFFFF" ? "#FF3B30" : cat.hex}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* KEY TAKEAWAYS */}
                {post.key_takeaways && post.key_takeaways.length > 0 && (
                    <div
                        className="mt-12 border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-6"
                        data-testid="key-takeaways"
                    >
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4 flex items-center gap-2">
                            <Shield size={12} /> KEY TAKEAWAYS
                        </div>
                        <ul className="space-y-3">
                            {post.key_takeaways.map((k, i) => (
                                <li key={i} className="font-sans text-base text-white/90 flex gap-3">
                                    <span className="font-anton text-2xl text-[#FF3B30] leading-none">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>{k}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* END-OF-ARTICLE LEAD FORM */}
                <div className="mt-12" data-testid="end-article-lead">
                    <BlogLeadForm
                        slug={post.slug}
                        topicKey={post.topic_key}
                        category={cat.label}
                        variant="inline"
                        slot="end"
                        accent="#FF3B30"
                    />
                </div>

                {/* CTA — 10-bot analysis */}
                <div className="mt-8 border-2 border-white p-8 text-center" data-testid="post-cta">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60 mb-3">
                        OR — RUN YOUR FULL SITUATION THROUGH THE 10 BOTS
                    </div>
                    <h3 className="font-anton text-3xl sm:text-4xl uppercase">
                        Free analysis.<br />
                        Real <span className="text-[#FF3B30]">precedents</span>. Real strategy.
                    </h3>
                    <p className="font-sans text-white/70 mt-4 max-w-md mx-auto">
                        Education tells you what to think about. The 10-bot analysis tells you exactly
                        what motions to file, what evidence to preserve, and which precedents apply.
                    </p>
                    <Link to="/hpm-submit" className="btn-brutal btn-brutal-red mt-6">
                        START FREE 10-BOT ANALYSIS <ArrowRight size={16} />
                    </Link>
                </div>

                {/* SHARE BAR (bottom) */}
                <div className="mt-10">
                    <BlogShareBar slug={post.slug} title={post.title} accent="#FF3B30" />
                </div>

                {/* NEWSLETTER SIGNUP */}
                <div className="mt-10" data-testid="blogpost-newsletter">
                    <NewsletterSignup
                        source="blog"
                        accent="#FFCC00"
                        compact
                        headline="Don't read once. Get armed every week."
                        subheadline="HPM Weekly Brief — questions worth asking, statutes cited, free resources, in your language. Free. Every Sunday."
                    />
                </div>

                {/* DISCLAIMER */}
                <div className="mt-12 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 border-t border-white/10 pt-6">
                    DISCLAIMER — {meta?.disclaimer || ""}
                </div>
            </article>

            <StickyLeadDrawer
                slug={post.slug}
                topicKey={post.topic_key}
                category={cat.label}
                accent="#FF3B30"
            />

            <Footer disclaimer="" />
        </div>
    );
}
